"use client";

import { Loader2, Mail } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestPasswordReset } from "@/lib/actions/users";

export function ForgotPasswordForm() {
    const searchParams = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setIsLoading(true);
        setError(null);

        const formData = new FormData(event.currentTarget);
        const email = (formData.get("email") as string) || "";

        const result = await requestPasswordReset(email);

        if (!result.success) {
            setError(result.error);
            setIsLoading(false);
            return;
        }

        setSuccess(true);
        setIsLoading(false);
    }

    return (
        <div className="w-full">
            <Card className="w-full glass-card rounded-[32px] border border-white/10 bg-[#0A0D14]/60 backdrop-blur-2xl shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] relative overflow-hidden group/card text-white">
                <div className="absolute -top-[20%] -left-[20%] w-[60%] h-[60%] bg-blue-500/10 blur-[80px] rounded-full pointer-events-none group-hover/card:bg-blue-500/15 transition-colors duration-700" />
                <div className="absolute -bottom-[20%] -right-[20%] w-[60%] h-[60%] bg-cyan-500/10 blur-[80px] rounded-full pointer-events-none group-hover/card:bg-cyan-500/15 transition-colors duration-700" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-50" />
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-30" />

                <div className="relative z-10 p-6 pt-8">
                    <CardHeader className="p-0 mb-6 space-y-2">
                        <CardTitle className="text-2xl font-bold tracking-tight text-center text-white uppercase font-sans">Passwort zurücksetzen</CardTitle>
                        <CardDescription className="text-center text-gray-400">
                            Wir senden Ihnen einen Link zum Zurücksetzen des Passworts.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0 space-y-3">
                        {error && (
                            <div className="p-3 text-sm text-red-200 bg-red-500/10 rounded-md border border-red-500/20 animate-in fade-in slide-in-from-top-1 text-center">
                                {error}
                            </div>
                        )}
                        {success ? (
                            <div className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 text-center">
                                Wenn die E-Mail-Adresse existiert, wurde ein Link zum Zurücksetzen gesendet.
                            </div>
                        ) : (
                            <form onSubmit={onSubmit} className="space-y-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="email" className="text-xs font-medium text-gray-100 uppercase tracking-wider ml-1">Email</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="name@example.com"
                                        required
                                        disabled={isLoading}
                                        defaultValue={searchParams.get('email') || ''}
                                        className="h-10 border-white/10 bg-[#0A0E17]/60 text-white placeholder:text-gray-500 focus:bg-[#0F1420]/80 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 rounded-lg transition-all"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="h-11 w-full bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 hover:from-blue-500 hover:via-blue-400 hover:to-cyan-400 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 active:scale-[0.98] transition-all duration-300 mt-4 border-t border-white/10"
                                    disabled={isLoading}
                                >
                                    {isLoading ? <Loader2 className="mr-3 h-5 w-5 animate-spin" /> : <Mail className="mr-3 h-5 w-5" />}
                                    <span className="tracking-wide">Link anfordern</span>
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </div>
            </Card>

            <div className="mt-6 text-center space-y-4">
                <div className="text-gray-300 text-sm">
                    <Link className="text-sky-400 font-bold hover:text-sky-300 transition-all hover:drop-shadow-[0_0_5px_rgba(56,189,248,0.5)] hover:underline underline-offset-4" href="/login">
                        Zur Anmeldung
                    </Link>
                </div>
            </div>
        </div>
    );
}
